import { auth } from "../libs/api-url";
import axios from "@/libs/axios";

/**
 * Admin Auth Service
 * Handles all admin authentication API calls based on the backend API documentation.
 * 
 * API Base URL: /api/v1/admin
 * Endpoints:
 * - POST /auth/login - Login
 * - POST /auth/forgot-password - Forgot Password
 * - POST /auth/change-password - Change Password (requires auth)
 * - PUT /auth/profile - Update Profile (requires auth)
 * - GET /auth/profile/get - Get Profile (requires auth)
 * - POST /auth/logout - Logout (requires auth)
 */
class UserService {
  /**
   * @name login
   * @description Logs in the user with the provided credentials by sending a POST request to the login API endpoint.
   * @param {Object} request - The request object containing the user's credentials (e.g., email and password).
   * @throws {Error} If the login request fails.
   * @returns {Promise<Object>} The response data containing user login information if successful.
   */
  static login(request) {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await axios.post(`${auth.login}`, request);
        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * @name forgotPassword
   * @description Initiates the process of resetting a user's password by sending a POST request to the forgot password API endpoint.
   * @param {Object} request - The request object containing the user's email.
   * @throws {Error} If the forgot password request fails.
   * @returns {Promise<Object>} The response data from the password reset initiation.
   */
  static forgotPassword(request) {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await axios.post(`${auth.forgotPassword}`, request);
        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * @name changePassword
   * @description Updates the user's password by sending a POST request to the change password API endpoint.
   * @param {Object} request - The request object containing the current and new password.
   * @throws {Error} If the password update request fails.
   * @returns {Promise<Object>} The response data from the password update request.
   */
  static changePassword(request) {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await axios.post(`${auth.changePassword}`, request);
        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * @name getProfile
   * @description Fetches the user profile information.
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data containing the user profile details.
   */
  static async getProfile() {
    try {
      const response = await axios.get(auth.getProfile);
      return response.data;
    } catch (err) {
      throw err; // Re-throw the error for the caller to handle
    }
  }

  /**
   * @name editProfile
   * @description Sends a request to update the user profile with the provided data.
   * The request is sent as multipart form data using PUT method.
   * @param {Object} request - The profile data to be updated.
   * @throws {Error} If the request fails, the error is rejected for the caller to handle.
   * @returns {Promise<Object>} A promise that resolves with the updated profile data.
   */
  static editProfile(request) {
    return new Promise(async (resolve, reject) => {
      try {
        const formData = new FormData();
        Object.keys(request).forEach((key) => {
          if (request[key] !== undefined && request[key] !== null) {
            formData.append(key, request[key]);
          }
        });

        const data = await axios.put(`${auth.updateProfile}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * @name resendForgotOtp
   * @description Resends the OTP for forgot password flow.
   * @param {Object} request - The request object containing the user's email.
   * @throws {Error} If the resend OTP request fails.
   * @returns {Promise<Object>} The response data containing the new encodedToken.
   */
  static resendForgotOtp(request) {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await axios.post(`${auth.resendForgotOtp}`, request);
        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * @name verifyOtp
   * @description Verifies the OTP with the provided encodedToken.
   * @param {Object} request - The request object containing encodedToken and otp.
   * @throws {Error} If the OTP verification request fails.
   * @returns {Promise<Object>} The response data containing resetToken if successful.
   */
  static verifyOtp(request) {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await axios.post(`${auth.verifyOtp}`, request);
        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * @name resetPassword
   * @description Resets the user's password using the resetToken.
   * @param {Object} request - The request object containing resetToken and new_password.
   * @throws {Error} If the password reset request fails.
   * @returns {Promise<Object>} The response data from the password reset request.
   */
  static resetPassword(request) {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await axios.post(`${auth.resetPassword}`, request);
        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * @name logout
   * @description Logs out the current user by sending a POST request to the logout API endpoint.
   * Requires Bearer token authentication.
   * @throws {Error} If the logout request fails.
   * @returns {Promise<Object>} The response data from the logout request.
   */
  static logout() {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await axios.post(`${auth.logout}`);
        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * @name sendEmailVerificationOtp
   * @description Sends OTP to the provided email for verification.
   * @param {Object} request - The request object containing the email.
   * @throws {Error} If the send email verification OTP request fails.
   * @returns {Promise<Object>} The response data from the send email verification OTP request.
   */
  static sendEmailVerificationOtp(request) {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await axios.post(`${auth.sendEmailVerificationOtp}`, request);
        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * @name verifyEmailOtp
   * @description Verifies the OTP for email verification.
   * @param {Object} request - The request object containing otp and email.
   * @throws {Error} If the verify email OTP request fails.
   * @returns {Promise<Object>} The response data from the verify email OTP request.
   */
  static verifyEmailOtp(request) {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await axios.post(`${auth.verifyEmailOtp}`, request);
        resolve(data.data);
      } catch (err) {
        reject(err);
      }
    });
  }
}

export { UserService };
